import { useState } from "react";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";
import {ReferralStats} from "../../types";

const FALLBACK_CODE = "SURPRISE25"; // Default referral code if user data is not available

interface Props {
  stats: ReferralStats | null;
  loading: boolean;
  error: boolean;
}

const ReferralStatus = ({ stats, loading, error }: Props) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();

  // Generate a referral code based on username (or fallback if not available)
  const referralCode = user?.username ?
    `${user.username.toUpperCase()}${new Date().getFullYear()}` :
    FALLBACK_CODE;


  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard",
    });

    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform: string) => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          "Join SurpriseTable and get €5 off your first booking! Use my referral code:"
        )}&url=${encodeURIComponent(referralLink)}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(
          `Join SurpriseTable and get €5 off your first booking! Use my referral code: ${referralCode}. ${referralLink}`
        )}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(
          "Discover Surprise Dining with SurpriseTable"
        )}&body=${encodeURIComponent(
          `Join SurpriseTable and get €5 off your first booking! Use my referral code: ${referralCode}. ${referralLink}`
        )}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  return (
    <div className="md:w-1/2 p-8 md:p-12 bg-white/80 dark:bg-gray-800/80">
      <h3 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
        Your Referral Status
      </h3>

      <div className="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <h4 className="mb-4 font-medium text-gray-800 dark:text-white">
          Your Unique Code
        </h4>
        <div className="flex">
          <div className="flex-1 rounded-l-md border border-gray-300 bg-gray-100 px-4 py-3 font-mono text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {referralCode}
          </div>
          <Button
            onClick={handleCopyReferral}
            className="rounded-l-none rounded-r-md bg-primary px-4 py-3 font-medium text-white hover:bg-red-700"
          >
            {copied ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
            )}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-medium text-gray-800 dark:text-white">
            Share Your Link
          </h4>
        </div>
        <div className="flex space-x-4">
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 rounded-full bg-blue-500 text-white hover:bg-blue-600"
            onClick={() => handleShare("facebook")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
            </svg>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 rounded-full bg-blue-400 text-white hover:bg-blue-500"
            onClick={() => handleShare("twitter")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
            </svg>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 rounded-full bg-green-500 text-white hover:bg-green-600"
            onClick={() => handleShare("whatsapp")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
            </svg>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12 rounded-full bg-pink-600 text-white hover:bg-pink-700"
            onClick={() => handleShare("email")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <h4 className="mb-4 font-medium text-gray-800 dark:text-white">
          Your Rewards
        </h4>

        {loading ? (
          <div className="py-6 flex justify-center">
            <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : stats ? (
          <>
            <div className="mb-4 flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">
                Successful Referrals
              </span>
              <span className="font-bold text-gray-800 dark:text-white">
                {stats.completed_referrals}
              </span>
            </div>
            <div className="mb-4 flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">
                Pending Referrals
              </span>
              <span className="font-bold text-gray-800 dark:text-white">
                {stats.pending_referrals}
              </span>
            </div>
            <div className="mb-4 flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">
                Total Credit Earned
              </span>
              <span className="font-bold text-gray-800 dark:text-white">
                €{stats.total_rewards.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">
                Available Credit
              </span>
              <span className="font-bold text-green-600 dark:text-green-400">
                €{stats.available_rewards.toFixed(2)}
              </span>
            </div>
          </>
        ) : error ? (
            <div className="py-2 text-center text-red-500 dark:text-red-400">
              Failed to load referral data. Please try again later.
            </div>
        ) : (
            <div className="py-2 text-center text-gray-500 dark:text-gray-400">
              No referral data available
            </div>
        )}
      </div>
    </div>
  );
};

export default ReferralStatus;
